import { HttpClient } from "@angular/common/http"
import { Injectable, InjectionToken, inject } from "@angular/core"
import { Centrifuge, FilterNode, UnauthorizedError, Subscription, disconnectedCodes } from "centrifuge"
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription as RxSubscription } from "rxjs"
import Bugsnag from "@bugsnag/js"

export interface SubscribeOptions {
  // Payload sent to the subscribe endpoint when minting a per-channel token.
  data?: any
  // User-limited channel (e.g. notif:#<id>, profile:#<id>): authorized by the
  // connection token's `sub`, so no per-channel token is requested.
  userLimited?: boolean
  // Optional server-side tag filter for user-limited channels (e.g. org_id).
  tagsFilter?: FilterNode
}

export interface RealtimeConfig {
  // Endpoints that mint Centrifugo tokens. Default to the app_api/club_api shape;
  // tv_api passes '/api/realtime/...' (no v1 namespace).
  connectUrl?: string
  subscribeUrl?: string
  // Override the websocket URL. Defaults to the current host so it works across
  // the member app's per-club subdomains.
  wsUrl?: string
}

export const REALTIME_CONFIG = new InjectionToken<RealtimeConfig>('REALTIME_CONFIG')

export class RealtimeChannel {
  private _sub: RxSubscription

  constructor (public sub: Subscription, public subject: Observable<any>) {}

  subscribe (fxn: (data: any) => void) {
    this._sub = this.subject.subscribe(fxn)
    return this
  }

  unsubscribe () {
    this._sub?.unsubscribe()
    this.sub?.unsubscribe()
    this._sub = this.sub = undefined
    return this
  }
}

@Injectable({providedIn: 'root'})
export class CentrifugoService {
  private centrifuge: Centrifuge
  private connectionState$ = new BehaviorSubject<boolean>(false)
  public isConnected$ = this.connectionState$.asObservable()

  private httpClient = inject(HttpClient)
  private config = inject(REALTIME_CONFIG, { optional: true })

  private readonly connectUrl = this.config?.connectUrl ?? '/api/v1/realtime/connect'
  private readonly subscribeUrl = this.config?.subscribeUrl ?? '/api/v1/realtime/subscribe'
  private readonly wsUrl = this.config?.wsUrl ?? `wss://${window.location.host}/realtime/connection/websocket`

  // Lazily establish the connection on first use. Unlike the club dashboard, the
  // member app has unauthenticated routes, so we must not connect until something
  // actually subscribes (by which point the user is logged in).
  private ensureConnected () {
    if (this.centrifuge) { return }

    this.centrifuge = new Centrifuge(this.wsUrl, {
      getToken: async (): Promise<string> => {
        try {
          const {token} = await firstValueFrom(this.httpClient.post<{ token: string }>(this.connectUrl, {}))
          return token
        } catch (e: any) {
          if (e.status === 401) { throw new UnauthorizedError(`Connect expired`) }
          // Status 0 = no network (e.g. mobile resuming before the radio is back); centrifuge retries with backoff.
          if (e?.status !== 0) {
            Bugsnag.notify(new Error('Centrifugo connect token failed'), (event) => event.addMetadata('realtime', { status: e?.status }))
          }
          throw e
        }
      }
    })

    this.centrifuge.on('connected', () => this.connectionState$.next(true))
    // errors aren't really errors here
    this.centrifuge.on('error', () => this.connectionState$.next(false))
    // 'disconnected' is terminal: the library only emits it when it will NOT reconnect by itself
    // (transient drops go through 'connecting' and retry with backoff).
    this.centrifuge.on('disconnected', (ctx) => {
      this.connectionState$.next(false)
      if (ctx.code === disconnectedCodes.disconnectCalled) { return }
      // Our own getToken threw UnauthorizedError on a 401 — an expired session, handled by the app's 401 flow.
      if (ctx.code === disconnectedCodes.unauthorized) { return }
      // Server gave up on a handshake that never completed — a tab that was asleep or throttled.
      // Centrifugo treats it as terminal, so bring the connection back ourselves.
      if (ctx.reason === 'stale') { this.reconnectWhenVisible(); return }
      Bugsnag.notify(new Error(`Centrifuge disconnected: ${ctx.reason}`), (event) => event.addMetadata('realtime', ctx))
    })

    this.centrifuge.connect()
  }

  connect () { this.ensureConnected() }

  // Reconnect now if the page is visible, otherwise when it next becomes visible — reconnecting
  // from a still-sleeping tab would only go stale again. No-op if disconnect() ran in the meantime.
  private reconnectWhenVisible () {
    if (!document.hidden) {
      setTimeout(() => this.centrifuge?.connect(), 1000)
      return
    }
    const onVisible = () => {
      if (document.hidden) { return }
      document.removeEventListener('visibilitychange', onVisible)
      this.centrifuge?.connect()
    }
    document.addEventListener('visibilitychange', onVisible)
  }

  subscribe (channel: string, opts: SubscribeOptions = {}): RealtimeChannel {
    this.ensureConnected()

    const subject = new Subject()
    let sub = this.centrifuge.getSubscription(channel)
    if (sub) {
      sub.on('publication', (ctx) => subject.next(ctx.data))
      return new RealtimeChannel(sub, subject.asObservable())
    }

    const subOptions: any = {}
    if (opts.userLimited) {
      // Authorized by the connection token's `sub` — no per-channel token.
      if (opts.tagsFilter) { subOptions.tagsFilter = opts.tagsFilter }
    } else {
      subOptions.getToken = async () => {
        return firstValueFrom(this.httpClient.post<{ token: string }>(this.subscribeUrl, {channel, data: opts.data ?? {}}))
          .then(r => r.token)
          .catch((e) => {
            if (e.status === 401) { throw new UnauthorizedError(`Session expired: ${channel}`) }
            Bugsnag.notify(new Error('Centrifugo subscribe token failed'), (event) => event.addMetadata('realtime', { channel, status: e?.status }))
            throw e
          })
      }
    }

    sub = this.centrifuge.newSubscription(channel, subOptions)
    sub.on('publication', (ctx) => subject.next(ctx.data))
    sub.on('error', (ctx) => {
      // 109 = token expired: a resubscribe reused the cached channel token past its
      // 1h exp. centrifuge-js refreshes via getToken and resubscribes by itself.
      if ((ctx as any).error?.code === 109) { return }
      // e.g. a permission denial — usually means the connection is authed as a
      // different user than the channel (a stale connection after login switch).
      Bugsnag.notify(new Error(`Centrifuge subscription error: ${channel}`), (event) => event.addMetadata('realtime', ctx))
    })
    sub.subscribe()

    return new RealtimeChannel(sub, subject.asObservable())
  }

  unsubscribe (channel: string) {
    const sub = this.centrifuge?.getSubscription(channel)
    if (sub) {
      sub.unsubscribe()
      this.centrifuge.removeSubscription(sub)
    }
  }

  // Fully tears down the connection. Null the instance so the next subscribe()
  // rebuilds it and re-fetches a fresh token — required on logout so a new login
  // doesn't reuse the previous user's connection identity.
  disconnect () {
    this.centrifuge?.disconnect()
    this.centrifuge = undefined
    this.connectionState$.next(false)
  }
}
